import { PayloadAction, createSlice, isAnyOf, current } from '@reduxjs/toolkit';
import { DownloadECGItem, ValueOf, DBEcgItem, StudyTableItem, StudyStatisticResponseDto } from 'types/types';
import { DataStatus, ViewMode } from 'types/enums';
import { getDetailedEcgData, getPatientsStudyData, getStudyStatistic } from './actions';

const INITIAL_RECORDS_LIMIT = 10;

type TableNavigation = {
  limit: number;
  skip: number;
};

const tableNavigation: TableNavigation = {
  limit: INITIAL_RECORDS_LIMIT,
  skip: 0,
};

type State = {
  status: ValueOf<typeof DataStatus>;
  studyFacilityId: string | null;
  studyTableData: StudyTableItem[];
  dbECGData: DBEcgItem | null;
  downloadECGData: DownloadECGItem | null;
  studyTotal: number;
  studyViewMode: ViewMode;
  tableNavigation: TableNavigation;
  studyStatistic: null | StudyStatisticResponseDto;
};

const initialState: State = {
  studyTableData: [],
  status: DataStatus.IDLE,
  dbECGData: null,
  downloadECGData: null,
  studyTotal: 0,
  studyViewMode: ViewMode.IDLE,
  tableNavigation,
  studyFacilityId: null,
  studyStatistic: null,
};

const { reducer, actions } = createSlice({
  initialState,
  name: 'study',
  reducers: {
    clearECGData: (state) => {
      state.dbECGData = null;
      state.downloadECGData = null;
    },
    setDownloadECGData: (state, { payload }: PayloadAction<DownloadECGItem | null>) => {
      state.downloadECGData = payload;
    },
    setStudyViewMode: (state, { payload }: PayloadAction<ViewMode>) => {
      state.studyViewMode = payload;
    },
    setTableNavigation: (state, { payload }: PayloadAction<TableNavigation>) => {
      state.tableNavigation = payload;
    },
    setStudyFacilityId: (state, { payload }: PayloadAction<string | null>) => {
      state.studyFacilityId = payload;
    },
    clearStudyData: (state) => {
      state.studyTableData = [];
      state.studyTotal = 0;
      state.studyFacilityId = null;
    },
  },
  extraReducers(builder) {
    builder.addCase(getPatientsStudyData.fulfilled, (state, { payload }) => {
      state.studyTableData = payload.patients;
      state.studyTotal = payload.totalCount;
      state.studyStatistic = payload.studyStatistic;
      state.status = DataStatus.SUCCEEDED;
    });

    builder.addCase(getDetailedEcgData.fulfilled, (state, { payload }) => {
      const { everbeatData, referenceData } = payload;
      const caregiverEcgs = current(state.studyTableData).find((info) => info.id === everbeatData.id);
      state.status = DataStatus.SUCCEEDED;
      state.downloadECGData = referenceData;
      state.dbECGData = {
        sampleRate: everbeatData.sampleRate,
        submittedTimestamp: everbeatData.submittedTimestamp,
        createdAt: everbeatData.createdAt,
        ecgLead: everbeatData.ecgLead,
        id: everbeatData.id,
        ecgCaptureId: caregiverEcgs.id,
        samples: everbeatData.samples,
        patientName: `${caregiverEcgs.firstName} ${caregiverEcgs.lastName}`,
        medicalRecordNumber: caregiverEcgs.medicalRecordNumber,
        assessment: everbeatData.assessment,
        device: everbeatData.device,
        heartRate: everbeatData.heartRate,
      };
    });
    builder.addCase(getStudyStatistic.fulfilled, (state, { payload }) => {
      state.studyStatistic = payload;
    });
    builder.addMatcher(isAnyOf(getDetailedEcgData.pending, getPatientsStudyData.pending), (state) => {
      state.status = DataStatus.LOADING;
    });
    builder.addMatcher(isAnyOf(getDetailedEcgData.rejected, getPatientsStudyData.rejected), (state) => {
      state.status = DataStatus.FAILED;
    });
  },
});

export { actions, reducer };
